import React, { useEffect, useId, useRef } from 'react';

const SCRIPT_ID = 'google-recaptcha-v2';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

let scriptPromise = null;

const loadRecaptchaScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);

  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', () => resolve(window.grecaptcha));
        existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')));
        if (window.grecaptcha?.render) resolve(window.grecaptcha);
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.grecaptcha);
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
      document.body.appendChild(script);
    });
  }

  return scriptPromise;
};

/**
 * Google reCAPTCHA v2 checkbox widget.
 * Calls onChange(token|null) when the user completes / expires the challenge.
 */
const RecaptchaV2 = ({ siteKey, onChange, className = '' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const reactId = useId().replace(/:/g, '');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined;

    let cancelled = false;

    const renderWidget = async () => {
      try {
        const grecaptcha = await loadRecaptchaScript();
        if (cancelled || !containerRef.current) return;

        await new Promise((resolve) => {
          if (grecaptcha.ready) {
            grecaptcha.ready(resolve);
          } else {
            resolve();
          }
        });

        if (cancelled || !containerRef.current) return;

        if (widgetIdRef.current != null) {
          try {
            grecaptcha.reset(widgetIdRef.current);
          } catch {
            /* ignore */
          }
          return;
        }

        containerRef.current.innerHTML = '';
        widgetIdRef.current = grecaptcha.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onChangeRef.current?.(token || null),
          'expired-callback': () => onChangeRef.current?.(null),
          'error-callback': () => onChangeRef.current?.(null),
        });
      } catch (err) {
        console.error(err);
        onChangeRef.current?.(null);
      }
    };

    renderWidget();

    return () => {
      cancelled = true;
    };
  }, [siteKey, reactId]);

  if (!siteKey) {
    return (
      <p className="text-sm text-red-600">
        reCAPTCHA site key is missing. Set RECAPTCHA_V2_SITE_KEY on the server.
      </p>
    );
  }

  return (
    <div className={className}>
      <div ref={containerRef} data-recaptcha-id={reactId} />
    </div>
  );
};

export const resetRecaptcha = () => {
  try {
    if (window.grecaptcha?.reset) window.grecaptcha.reset();
  } catch {
    /* ignore */
  }
};

export default RecaptchaV2;
